import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { sharedViewsEndpoint } from "@/config/endpoints";
import type {
  ListShareSnapshot,
  SharedListView,
  SharedViewRecipient,
  ShareResult,
} from "../types";

export const sharedViewKey = (token: string) => ["shared-view", token] as const;
export const sharedViewRecipientsKey = (token: string) =>
  ["shared-view", token, "recipients"] as const;

export function useSharedView(token: string | undefined) {
  return useQuery({
    queryKey: sharedViewKey(token ?? ""),
    enabled: Boolean(token),
    staleTime: Infinity,
    retry: 0,
    queryFn: async () => {
      const res = await axiosInstance.get(`${sharedViewsEndpoint}/${token}`);
      return res.data?.data as SharedListView;
    },
  });
}

export function useCreateSharedView() {
  return useMutation({
    mutationFn: async (input: {
      label: string;
      kind: ListShareSnapshot["source"]["kind"];
      payload: ListShareSnapshot;
    }) => {
      const res = await axiosInstance.post(sharedViewsEndpoint, input);
      return res.data?.data as SharedListView;
    },
  });
}

/** Who this link has already been shared with (admin-only endpoint). */
export function useSharedViewRecipients(token: string | null) {
  return useQuery({
    queryKey: sharedViewRecipientsKey(token ?? ""),
    enabled: Boolean(token),
    staleTime: 0,
    retry: 0,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `${sharedViewsEndpoint}/${token}/recipients`,
      );
      return (res.data?.data ?? []) as SharedViewRecipient[];
    },
  });
}

/**
 * Hand the link to Xale users of this workspace by email. The server resolves
 * each address to a user of the tenant — anything it couldn't deliver comes
 * back in `skipped` with the reason, so the dialog reports per address instead
 * of a blanket "done".
 */
export function useShareSharedView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      token: string;
      emails: string[];
      message?: string;
    }) => {
      const { token, ...body } = input;
      const res = await axiosInstance.post(
        `${sharedViewsEndpoint}/${token}/share`,
        body,
      );
      return res.data?.data as ShareResult;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: sharedViewRecipientsKey(vars.token),
      });
    },
  });
}
